/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useMemo } from 'react';
import { DateRange, defaultStaticRanges, DefinedRange } from 'react-date-range';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import {
  STAY_DATE_END_DATE,
  STAY_DATE_START_DATE,
  YEAR_MONTH_DAY_PATTERN,
} from 'modules/common/constants/date-range';
import variants from 'modules/common/theme/variants';
import customPresets from 'modules/common/constants/custom-presets';
import { selectAppTheme } from 'modules/dashboard/selectors';
import { Box } from '@mui/material';
import { format } from 'date-fns';
import {
  selectFavoriteScenarios,
  selectScenarios,
  selectSearchData,
  selectSelectedScenario,
} from '../../selectors';
import { searchActions } from '../../slice';
import { getDateRange } from '../../functions';

/**
 * Date range picker used to filter stay dates in search searchScenarios.
 *
 * @param {Object} filterData
 * @returns {JSX.Element}
 */
const SearchStayDateRangePicker = ({ filterData, found }) => {
  const dispatch = useDispatch();
  const scenarios = useSelector(selectScenarios);
  const searchScenarios = useSelector(selectSearchData);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const themeName = useSelector(selectAppTheme);
  const selectedScenario = useSelector(selectSelectedScenario);
  // Get the currently selected theme variant for coloring the picker
  const selectedVariant = useMemo(
    () => variants.find((variant) => variant.name === themeName),
    [themeName]
  );
  // Build the date range object for the date picker
  const dateRange = useMemo(() => {
    let startDate;
    let endDate;

    if (typeof found.value === 'string') {
      const range = getDateRange(found.value);
      //
      startDate = range?.startDate;
      endDate = range?.endDate;
    } else {
      startDate = found.value.startDate;
      endDate = found.value.endDate;
    }
    return [
      {
        startDate: new Date(startDate ?? new Date()),
        endDate: new Date(endDate) ?? new Date(),
        key: 'selection',
        color: selectedVariant?.palette.primary.main,
      },
    ];
  }, [found, selectedVariant]);
  // Handle date changes and update searchScenarios in the store
  const handleChange = (item) => {
    if (!found) return;
    const formattedDateRange = `${format(
      item.selection.startDate,
      YEAR_MONTH_DAY_PATTERN
    )} - ${format(item.selection.endDate, YEAR_MONTH_DAY_PATTERN)}`;
    //
    if (scenarios?.results.length) {
      const updatedScenarios = scenarios.results.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, formattedDateRange),
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.entries(visual.filters).reduce((acc, [filterName, filter]) => {
              if (filter?.keyword === found?.keyword) {
                acc[filterName] = {
                  ...filter,
                  keyword: formattedDateRange,
                  value: {
                    startDate: item.selection.startDate?.toISOString(),
                    endDate: item.selection.endDate?.toISOString(),
                  },
                };
              } else {
                acc[filterName] = filter;
              }
              return acc;
            }, {}),
          })),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setScenarios({ results: updatedScenarios }));
    }
    //
    if (searchScenarios.length) {
      const updatedScenarios = searchScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, formattedDateRange),
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.entries(visual.filters).reduce((acc, [filterName, filter]) => {
              if (filter?.keyword === found?.keyword) {
                acc[filterName] = {
                  ...filter,
                  keyword: formattedDateRange,
                  value: {
                    startDate: item.selection.startDate?.toISOString(),
                    endDate: item.selection.endDate?.toISOString(),
                  },
                };
              } else {
                acc[filterName] = filter;
              }
              return acc;
            }, {}),
          })),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setSearchScenario(updatedScenarios));
    }
    //
    if (favoriteScenarios.length) {
      const updatedScenarios = favoriteScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, formattedDateRange),
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.entries(visual.filters).reduce((acc, [filterName, filter]) => {
              if (filter?.keyword === found?.keyword) {
                acc[filterName] = {
                  ...filter,
                  keyword: formattedDateRange,
                  value: {
                    startDate: item.selection.startDate?.toISOString(),
                    endDate: item.selection.endDate?.toISOString(),
                  },
                };
              } else {
                acc[filterName] = filter;
              }
              return acc;
            }, {}),
          })),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setFavoriteScenario(updatedScenarios));
    }
  };
  //
  return (
    <Box sx={{ minWidth: '600px', minHeight: '420px', bgcolor: 'transparent' }}>
      <DefinedRange
        onChange={handleChange}
        ranges={dateRange}
        className={styles.calendarElement3}
        inputRanges={[]}
        staticRanges={[
          defaultStaticRanges[0],
          customPresets[0],
          defaultStaticRanges[2],
          defaultStaticRanges[4],
          customPresets[3],
          customPresets[1],
          customPresets[2],
        ]}
      />
      <DateRange
        id="search-stay-date-range-picker"
        onChange={handleChange}
        editableDateInputs
        moveRangeOnFirstSelection={false}
        ranges={dateRange}
        months={1}
        fixedHeight
        direction="horizontal"
        className={styles.calendarElement}
        maxDate={new Date(STAY_DATE_END_DATE)}
        minDate={new Date(STAY_DATE_START_DATE)}
        dateDisplayFormat="yyyy-MM-dd"
      />
    </Box>
  );
};

export default SearchStayDateRangePicker;
